<template>
  <b-card>
    <b-row>
      <!-- Project Info: Left col -->
      <b-col
        class="d-flex justify-content-between flex-column"
        cols="21"
        xl="6"
      >
        <!-- Project Avatar & Action Buttons -->
        <div class="d-flex justify-content-start">
          <b-avatar
            button
            rounded
            size="114px"
            variant="light"
            @click="pathToFileManager"
          >
            <div
              :class="{
                'text-primary': isWon,
                'text-secondary': isClosed,
                'text-danger': isLost,
              }"
              class="row"
            >
              <feather-icon
                class="col-12"
                height="45"
                icon="FolderIcon"
                width="45"
              />
              <span class="col-12">Archives</span>
            </div>
          </b-avatar>
          <div class="d-flex flex-column ml-1">
            <div class="mb-1">
              <h4
                :class="{
                  'text-primary': isWon,
                  'text-secondary': isClosed,
                  'text-danger': isLost,
                }"
                badge="4"
                class="mb-0"
              >
                {{ projectData.project_code }} <span v-if="isLost">(Perdu)</span
                ><span v-else-if="isClosed">(Clôturé)</span>
              </h4>
              <div class="d-flex flex-column">
                <div class="card-text">
                  <span
                    >{{ projectData.details.concerned_department }} -
                    <i>{{ formatDate(projectData.created_at) }}</i></span
                  >
                  <span v-if="isLost" class="text-danger ml-1">
                    <feather-icon icon="AlertTriangleIcon" />
                  </span>
                </div>
                <span class="card-text"
                  >Responsable :
                  {{
                    projectData.assignee ? projectData.assignee.name : ""
                  }}</span
                >
              </div>
            </div>
            <div class="d-flex flex-wrap">
              <b-button
                v-if="$can('edit_project', 'Projets')"
                :to="{
                  name: 'projects-edit',
                  params: { id: projectData.uid },
                }"
                variant="primary"
              >
                Modifier
              </b-button>
              <b-button
                v-if="$can('delete_project', 'Projets') && !isClosed"
                class="ml-1"
                variant="outline-danger"
                @click="deleteProject"
              >
                Supprimer
              </b-button>
            </div>
          </div>
        </div>

        <!-- Task stats -->
        <div class="d-flex align-items-center mt-2">
          <div class="d-flex align-items-center mr-2">
            <b-avatar rounded variant="light-primary">
              <feather-icon icon="RotateCwIcon" size="18" />
            </b-avatar>
            <div class="ml-1">
              <h5 class="mb-0">{{ projectData.task_pending }}</h5>
              <small>Tâches En Cours</small>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <b-avatar rounded variant="light-success">
              <feather-icon icon="CheckCircleIcon" size="18" />
            </b-avatar>
            <div class="ml-1">
              <h5 class="mb-0">{{ projectData.task_done }}</h5>
              <small>Tâches Terminées</small>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Right Col: Table -->
      <b-col cols="12" xl="6">
        <table class="mt-2 mt-xl-0 w-100">
          <tr class="text-nowrap">
            <th class="pb-50">
              <feather-icon class="mr-75" icon="AlertCircleIcon" />
              <span class="font-weight-bold">Statut</span>
            </th>
            <td
              class="pl-2 pb-50 text-uppercase text-truncate"
              style="max-width: 40%"
            >
              {{ projectData.status }}
            </td>
          </tr>
          <tr class="text-nowrap">
            <th class="pb-50">
              <feather-icon class="mr-75" icon="AlertCircleIcon" />
              <span class="font-weight-bold">Type de Projet</span>
            </th>
            <td
              class="pl-2 pb-50 text-uppercase text-truncate"
              style="max-width: 40%"
            >
              <b-badge variant="light-primary text-capitalize">
                {{ projectData.type }}
              </b-badge>
            </td>
          </tr>
          <tr class="text-nowrap">
            <th class="pb-50">
              <feather-icon class="mr-75" icon="UserIcon" />
              <span class="font-weight-bold">Client</span>
            </th>
            <td
              class="pl-2 pb-50 text-capitalize text-truncate"
              style="max-width: 40%"
            >
              {{ projectData.customer.name }}
            </td>
          </tr>
          <tr class="text-nowrap">
            <th class="pb-50">
              <feather-icon class="mr-75" icon="UnderlineIcon" />
              <span class="font-weight-bold">Bailleur</span>
            </th>
            <td
              class="pl-2 pb-50 text-capitalize text-truncate"
              style="max-width: 40%"
            >
              {{ projectData.details.lessor }}
            </td>
          </tr>
          <tr class="text-nowrap">
            <th class="pb-50">
              <feather-icon class="mr-75" icon="CalendarIcon" />
              <span class="font-weight-bold">Date de Dépôt</span>
            </th>
            <td
              class="pl-2 pb-50 text-capitalize text-truncate"
              style="max-width: 40%"
            >
              {{ projectData.details.deposit_date }}
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <feather-icon class="mr-75" icon="CompassIcon" />
              <span class="font-weight-bold">Localisation</span>
            </th>
            <td class="pl-2 pb-50">
              <b-button
                class="text-truncate p-0"
                variant="link"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                size="sm"
                :disabled="!hasLocations"
                @click="openMapModal"
              >
                {{ hasLocations ? "Voir sur la carte" : "Aucune localisation" }}
              </b-button>
            </td>
          </tr>
          <tr>
            <th>
              <feather-icon class="mr-75" icon="AtSignIcon" />
              <span class="font-weight-bold">Crée par</span>
            </th>
            <td class="pl-2 text-truncate" style="max-width: 40%">
              {{ projectData.creator.name }}
            </td>
          </tr>
        </table>
      </b-col>
    </b-row>
    <b-modal
      v-model="showMap"
      centered
      ok-only
      ok-title="Fermer"
      size="lg"
      title="Carte de Relevé topographique"
    >
      <multiple-maps :markers="projectLocations" />
    </b-modal>
  </b-card>
</template>

<script>
import axios from "@axios";
import { useRouter } from "@core/utils/utils";
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BModal,
  BRow,
} from "bootstrap-vue";
import moment from "moment";
import useProjectDetails from "./useProjectDetails";
import {computed, ref, toRefs} from "@vue/composition-api";
import Swal from "sweetalert2";
import Ripple from "vue-ripple-directive";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import MultipleMaps from "@/views/apps/project/maps/MultipleMaps.vue";

moment.locale("fr");

export default {
  components: {
    MultipleMaps,
    BModal,
    BCard,
    BButton,
    BRow,
    BCol,
    BAvatar,
    BBadge,
  },
  directives: {
    Ripple,
  },
  props: {
    projectData: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { route, router } = useRouter();
    const toast = useToast();
    const formatDate = (date) => moment(new Date(date).getTime()).fromNow();

    const showMap = ref(false);
    const { isWon, isLost, isClosed } = useProjectDetails(toRefs(props), emit);

    const hasLocations = computed(() => props.projectData.locations.length > 0)

    const projectLocations = computed(() => props.projectData.locations.map(location => ({
      ...location,
      code: props.projectData.code,
    })))
    const openMapModal = () => {
      if (!hasLocations) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Pas possible de visualiser la carte.",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }
      showMap.value = true;
    };

    //path to project file manager
    const pathToFileManager = () => {
      const uid = props.projectData.folder.uid;
      router.push({ name: "folder-detail", params: { folder: uid } });
    };

    // delete project
    const deleteProject = () => {
      Swal.fire({
        title: "Êtes vous sûr?",
        text: "Ce projet sera supprimé et cette action est irréversible!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, supprimer !",
        cancelButtonText: "Annuler",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-primary ml-1",
        },
        buttonsStyling: false,
        preConfirm() {
          const uid = props.projectData?.uid;
          return axios
            .delete(`projects/${uid}`, {
              headers: {
                Accept: "application/vnd.api+json",
                "Content-Type": "application/vnd.api+json",
              },
            })
            .then(({ data, status }) => {
              if (status != 200) throw new Error(data.message);
              return data;
            })
            .catch((error) => {
              Swal.showValidationMessage(
                "Une erreur est survenue lors de la suppression du projet."
              );
            });
        },
      }).then((result) => {
        if (result.value) {
          Swal.fire({
            icon: "success",
            title: "Supprimé!",
            text: "Le projet a été supprimé.",
            customClass: {
              confirmButton: "btn btn-success",
            },
          }).then((result) => {
            router.push({ name: "projects-list" });
          });
        }
      });
    };

    return {
      pathToFileManager,
      formatDate,
      hasLocations,
      openMapModal,
      projectLocations,
      showMap,
      isWon,
      isLost,
      isClosed,
      deleteProject,
    };
  },
};
</script>
